import './ListPage.scss';
import { Button, Dropdown, Icon, Modal, SimpleNumberCard, Typography } from '@design-system/index';
import { LargeIconButton } from '@design-system/components/Buttons/LargeIconButton/LargeIconButton';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { AddDashed, AppartementsLine, Pdf, RoundedArrow, ShopsSettingsLine, Warning } from '@icons';
import { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import moment from 'moment';
import { useGetBookingsMutation } from '@services/member/booking/booking.endpoints';
import { BookingStatus, BookingTable } from '@services/member/booking/booking.types';
import { BOOKING_STATUS } from '@utils/enumTranslation';
import { getRelativeDayLabel } from '@utils/dates';
import { formatMoney } from '@utils/numbers';
import { useListPageVM } from './useListPageVM';
import { getBookingAlerts } from './bookingAlerts';

const STATUSES: BookingStatus[] = ['upcoming', 'current', 'past', 'all'];

/** Au-delà, "Dans 412 jours" n'apporte rien : on n'affiche le libellé relatif que pour l'horizon proche. */
const RELATIVE_LABEL_MAX_DAYS = 30;

const CARD_LABELS: Record<BookingStatus, { single: string; multiple: string }> = {
    upcoming: { single: 'Réservation à venir', multiple: 'Réservations à venir' },
    current: { single: 'Réservation en cours', multiple: 'Réservations en cours' },
    past: { single: 'Réservation passée', multiple: 'Réservations passées' },
    all: { single: 'Réservation', multiple: 'Réservations' }
};

const SubLine = ({ children }: { children: ReactNode }) => (
    <Typography intrinsic="span" variant="p3" color="grey1" className="booking-cell-sub">
        {children}
    </Typography>
);

const relativeLabel = (date: string): string | null => {
    const diff = Math.abs(moment(date).startOf('day').diff(moment().startOf('day'), 'days'));
    return diff <= RELATIVE_LABEL_MAX_DAYS ? getRelativeDayLabel(date) : null;
};

const stayLabel = (booking: BookingTable): string => {
    const nights = moment(booking.departureDate).diff(moment(booking.arrivalDate), 'days');
    const parts = [`${nights} ${nights > 1 ? 'nuits' : 'nuit'}`];
    if (booking.peopleCount) {
        parts.push(`${booking.peopleCount} pers.`);
    }
    return parts.join(' · ');
};

export default function ListPage() {
    const {
        columnHelper,
        status,
        setStatus,
        apartmentId,
        handleApartmentChange,
        apartmentOptions,
        apartmentChoices,
        isApartmentsLoading,
        isApartmentsError,
        total,
        handleMetadata,
        goToBooking,
        goToApartmentPlanning,
        goToPlanning,
        isNewBookingModalOpen,
        newBookingApartment,
        setNewBookingApartment,
        newBookingError,
        openNewBookingModal,
        closeNewBookingModal,
        confirmNewBooking
    } = useListPageVM();

    const columns = [
        columnHelper.accessor((row) => row.apartment?.label ?? '-', {
            id: 'apartment.label',
            header: 'Logement',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => {
                const apartment = info.row.original.apartment;
                return (
                    <div className="booking-cell booking-cell-apartment">
                        <div className="booking-cell-apartment-text">
                            <span>{info.getValue()}</span>
                            {apartment?.reference && <SubLine>Réf. {apartment.reference}</SubLine>}
                        </div>
                        {apartment && (
                            <Button
                                variant="icon-hover"
                                color="grey-secondary"
                                title="Ouvrir le planning du logement"
                                icon={<Icon icon={<RoundedArrow />} size={20} />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    goToApartmentPlanning(apartment.id);
                                }}
                            />
                        )}
                    </div>
                );
            }
        }),
        columnHelper.accessor('member.cosemNumber', {
            id: 'member.cosemNumber',
            header: 'N° COSEM',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => <>{info.getValue()}</>
        }),
        columnHelper.accessor(
            (row) => `${row.member?.personalInformation?.lastname ?? ''} ${row.member?.personalInformation?.firstname ?? ''}`.trim(),
            {
                id: 'member.personalInformation.lastname',
                header: 'Adhérent',
                enableSorting: true,
                meta: { clickable: true },
                cell: (info) => <>{info.getValue().toUpperCase()}</>
            }
        ),
        columnHelper.accessor('arrivalDate', {
            header: 'Arrivée',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => {
                const hint = status === 'current' ? null : relativeLabel(info.getValue());
                return (
                    <div className="booking-cell">
                        <span>{moment(info.getValue()).format('DD/MM/YYYY')}</span>
                        {hint && <SubLine>{hint}</SubLine>}
                    </div>
                );
            }
        }),
        columnHelper.accessor('departureDate', {
            header: 'Départ',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => {
                const departureHint = status === 'current' ? relativeLabel(info.getValue()) : null;
                return (
                    <div className="booking-cell">
                        <span>{moment(info.getValue()).format('DD/MM/YYYY')}</span>
                        <SubLine>{departureHint ? `Départ ${departureHint.toLowerCase()} · ` : ''}{stayLabel(info.row.original)}</SubLine>
                    </div>
                );
            }
        }),
        columnHelper.accessor('rentalPrice', {
            id: 'rentalPrice',
            header: 'Prix',
            enableSorting: true,
            meta: { clickable: true },
            cell: (info) => <>{formatMoney(info.getValue())}</>
        }),
        columnHelper.display({
            id: 'alerts',
            header: 'Suivi',
            meta: { clickable: true },
            cell: (info) => {
                const alerts = getBookingAlerts(info.row.original);
                if (alerts.length === 0) {
                    return <>-</>;
                }
                return (
                    <div className="booking-alerts">
                        {alerts.map((alert) => (
                            <span key={alert.label} className={`booking-alert booking-alert-${alert.tone}`}>
                                <Icon icon={<Warning />} size={14} color={alert.tone === 'error' ? 'error' : 'secondary'} />
                                {alert.label}
                            </span>
                        ))}
                    </div>
                );
            }
        }),
        columnHelper.display({
            id: 'documents',
            size: 0,
            header: 'Documents',
            meta: { style: { textAlign: 'center' } },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                    {info.row.original.confirmationPdfLink && (
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            title="Confirmation de réservation"
                            icon={<Icon icon={<ShopsSettingsLine />} size={20} color="primary" />}
                            hoverIcon={<Icon icon={<ShopsSettingsLine />} size={20} color="secondary" />}
                            onClick={() => window.open(info.row.original.confirmationPdfLink, '_blank')}
                        />
                    )}
                    {info.row.original.pdfLink && (
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            title="Convention de location"
                            icon={<Icon icon={<Pdf />} size={20} color="primary" />}
                            hoverIcon={<Icon icon={<Pdf />} size={20} color="secondary" />}
                            onClick={() => window.open(info.row.original.pdfLink, '_blank')}
                        />
                    )}
                </div>
            )
        })
    ] as ColumnDef<BookingTable>[];

    return (
        <div className="bookings-table-container">
            <div className="bookings-table-container-header">
                <div className="bookings-table-container-header-left">
                    <SimpleNumberCard
                        labelForSingle={CARD_LABELS[status].single}
                        labelForMultiple={CARD_LABELS[status].multiple}
                        value={total}
                    />
                    <LargeIconButton icon={<AddDashed />} label="Nouvelle réservation" onClick={openNewBookingModal} />
                </div>
                <Button variant="secondary" onClick={goToPlanning}>
                    Voir le planning
                </Button>
            </div>

            <div className="bookings-toolbar">
                <div className="bookings-status-switch">
                    {STATUSES.map((value) => (
                        <Button key={value} variant={value === status ? 'primary' : 'secondary'} onClick={() => setStatus(value)}>
                            {BOOKING_STATUS[value]}
                        </Button>
                    ))}
                </div>
                <div className="bookings-apartment-select">
                    <Dropdown
                        label="Logement"
                        options={apartmentOptions}
                        value={apartmentId ?? 0}
                        isSearchable
                        disabled={isApartmentsLoading}
                        onOptionChange={handleApartmentChange}
                    />
                    {isApartmentsError && (
                        <Typography intrinsic="p" variant="p3" color="error">
                            La liste des logements n'a pas pu être chargée.
                        </Typography>
                    )}
                </div>
            </div>

            <MutationTable
                key={`${status}-${apartmentId ?? 'all'}`}
                columns={columns}
                mutationHook={useGetBookingsMutation}
                mutationHookParams={{ status, apartmentId }}
                onRowClick={goToBooking}
                defaultSorting={{
                    field: 'arrivalDate',
                    direction: status === 'past' || status === 'all' ? 'desc' : 'asc'
                }}
                headerTitle={`Liste des ${CARD_LABELS[status].multiple.toLowerCase()}`}
                headerIcon={<AppartementsLine />}
                onMetadata={handleMetadata}
                selectable={false}
                filters={[
                    {
                        id: 'arrivalDate',
                        label: "Date d'arrivée",
                        type: 'date',
                        comparatorTypes: ['isBetween', 'isBefore', 'isAfter']
                    },
                    {
                        id: 'departureDate',
                        label: 'Date de départ',
                        type: 'date',
                        comparatorTypes: ['isBetween', 'isBefore', 'isAfter']
                    },
                    {
                        id: 'paymentInPending',
                        label: 'Paiement en attente',
                        type: 'boolean'
                    }
                ]}
            />

            <Modal
                className="new-booking-modal"
                open={isNewBookingModalOpen}
                title="Nouvelle réservation"
                yesText="Continuer"
                noText="Annuler"
                closeOnValidate={false}
                onValidate={confirmNewBooking}
                onClose={closeNewBookingModal}
                setClose={closeNewBookingModal}
            >
                <Typography intrinsic="p" color="secondary">
                    Pour quel logement souhaitez-vous créer la réservation ?
                </Typography>
                {apartmentChoices.length > 0 ? (
                    <Dropdown
                        label="Logement"
                        options={apartmentChoices}
                        value={newBookingApartment?.id}
                        isSearchable
                        required
                        onOptionChange={(option) => setNewBookingApartment(option)}
                    />
                ) : (
                    <Typography intrinsic="p" variant="p2" color="grey1">
                        {isApartmentsLoading ? 'Chargement des logements…' : 'Aucun logement actif : créez d’abord un logement.'}
                    </Typography>
                )}
                {newBookingError && (
                    <Typography intrinsic="p" variant="p3" color="error">
                        {newBookingError}
                    </Typography>
                )}
            </Modal>
        </div>
    );
}
